// Top-level app shell: hydrates state, picks the active screen, and routes
// between discover / chat / matches / library / story-view / flashcards.
//
// Routing is intentionally state-driven for now (no React Router). Once we
// add deep-linking we'll swap in the router with the same screen names.

import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, GraduationCap, Heart, Library as LibraryIcon, MessageCircle, MessageSquare } from 'lucide-react';
import { DiscoverScreen } from '@/features/discover/DiscoverScreen';
import { ChatScreen } from '@/features/chat/ChatScreen';
import { MatchesScreen } from '@/features/matches/MatchesScreen';
import { PersonaProfileSheet } from '@/components/PersonaProfileSheet';
import { LibraryScreen } from '@/features/library/LibraryScreen';
import { FlashcardMode } from '@/features/library/FlashcardMode';
import { HSKReviewScreen } from '@/features/hsk-review/HSKReviewScreen';
import { HSKReviewFlashcards } from '@/features/hsk-review/HSKReviewFlashcards';
import type { HskReviewLevel } from '@/lib/hsk-review/types';
import { StoryViewer } from '@/features/stories/StoryViewer';
import { OnboardingScreen } from '@/features/onboarding/OnboardingScreen';
import { ProfileSetupScreen } from '@/features/onboarding/ProfileSetupScreen';
import { Toast } from '@/components/ui/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAppState } from '@/hooks/useAppState';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useDailyStories } from '@/hooks/useDailyStories';
import { SignInScreen } from '@/features/auth/SignInScreen';
import { ResetPasswordScreen } from '@/features/auth/ResetPasswordScreen';
import { UserMenu } from '@/features/auth/UserMenu';
import { ConflictPrompt } from '@/features/auth/ConflictPrompt';
import { SettingsSheet } from '@/features/auth/SettingsSheet';
import { SignInNudge } from '@/features/auth/SignInNudge';
import { FeedbackSheet } from '@/features/feedback/FeedbackSheet';
import { NotificationsSheet } from '@/features/notifications/NotificationsSheet';
import { getAuth } from '@/lib/auth/auth';
import { localRepository } from '@/lib/storage/localRepository';
import { SyncedRepository } from '@/lib/storage/syncedRepository';
import { applyResolution, type MergeResult } from '@/lib/storage/merge';
import type { Repository } from '@/lib/storage/repository';
import { storiesFor } from '@/data/stories';
import { PERSONAS, getPersona } from '@/data/personas';
import { HSKBuilder } from '@/features/hsk/HSKBuilder';
import { AppHeader } from '@/components/ui/AppHeader';
import type { DiscoverView } from '@/components/ui/DiscoverViewToggle';
import { buildCustomPersona } from '@/lib/hsk';
import { getAIClient } from '@/lib/ai/client';
import type { Persona } from '@/lib/types';
import { Events, identify, initObservability, track } from '@/lib/observability';
import { useSession } from '@/lib/session';
import { C } from '@/styles/tokens';

// Recharts + the dashboard chunk should never enter the main bundle. Lazy
// import so a regular user opening the app pays nothing for it.
const AdminDashboard = lazy(() => import('@/features/admin/AdminDashboard'));

type Tab = 'discover' | 'matches' | 'library' | 'hsk' | 'admin';

interface ActiveChat {
  personaId: string;
}

interface ActiveStory {
  personaId: string;
  idx: number;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export default function App() {
  const auth = useAuth();
  const userId = auth.user?.id ?? null;
  const [conflict, setConflict] = useState<MergeResult | null>(null);

  // Pick repository per auth state. SyncedRepository wraps localRepository,
  // so saves always hit IndexedDB; cloud PUT is a debounced add-on.
  const repository: Repository = useMemo(() => {
    if (!userId) return localRepository;
    return new SyncedRepository({
      endpoint: `${API_BASE}/sync`,
      getToken: () => getAuth().current()?.jwt ?? null,
      onMergeReport: (report) => {
        if (report.divergence.length > 0) setConflict(report);
      },
    });
  }, [userId]);

  const state = useAppState(repository);
  const { toast, push, dismiss } = useToast();
  const dailyStories = useDailyStories(PERSONAS);
  // session_start / session_end events drive the admin dashboard's "habit"
  // and "words per active hour" metrics. Mounted once at the root.
  useSession();

  // Default landing tab is Chats — that's the home screen users come back to.
  // Discover is one tap away for first-timers (and the empty Chats state
  // surfaces a "Find your HanZi friend" CTA that jumps straight there).
  const [tab, setTab] = useState<Tab>('matches');
  const [chat, setChat] = useState<ActiveChat | null>(null);
  const [story, setStory] = useState<ActiveStory | null>(null);
  const [flashcardOpen, setFlashcardOpen] = useState(false);
  const [hskReviewFlashcards, setHskReviewFlashcards] = useState<HskReviewLevel | null>(null);
  const [hskBuilderOpen, setHskBuilderOpen] = useState(false);
  const [creatingPersona, setCreatingPersona] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // Profile sheet opened from Browse mode. When set, the sheet renders with a
  // "Start chatting" CTA that auto-matches the persona and opens chat.
  const [profileOpen, setProfileOpen] = useState<Persona | null>(null);
  // Swipe vs Browse view for the Discover screen. Lifted here so the toggle
  // (now rendered inside the screen, below the AppHeader) can read/write it.
  const [discoverView, setDiscoverView] = useState<DiscoverView>('swipe');

  // Resolve a persona id against both curated and user-built personas.
  const findPersona = useCallback(
    (id: string): Persona | null => {
      return getPersona(id) ?? state.snapshot.customPersonas.find((p) => p.id === id) ?? null;
    },
    [state.snapshot.customPersonas],
  );

  const handleSignOut = useCallback(async () => {
    await getAuth().signOut();
    // Per design 3a: cloud is the source of truth, so wipe local on sign-out.
    // The repo switches back to localRepository on the next render and loads
    // EMPTY_SNAPSHOT — profile.level falls back to undefined and the user
    // re-enters onboarding (whose final step offers sign-in again).
    await localRepository.clear();
    state.setSkipAuth(false);
    setChat(null);
    setStory(null);
    setHskBuilderOpen(false);
    setFlashcardOpen(false);
    setHskReviewFlashcards(null);
    setTab('matches');
    push('Signed out', 'success');
  }, [state, push]);

  const handleReSignIn = useCallback(() => {
    state.setSkipAuth(false);
  }, [state]);

  // Header right slot — three top-level controls in a row: notifications
  // (announcements inbox), feedback (opens the rating sheet), then the
  // account/gear menu. Feedback used to live inside Settings + the account
  // popover; promoting it removes a tap so it actually gets used.
  const accountRight = (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setNotificationsOpen(true)}
        aria-label="Notifications"
        className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
        style={{ background: 'rgba(26,22,20,0.06)', color: C.ink }}
      >
        <Bell size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setFeedbackOpen(true)}
        aria-label="Send feedback"
        className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
        style={{ background: 'rgba(26,22,20,0.06)', color: C.ink }}
      >
        <MessageSquare size={16} aria-hidden />
      </button>
      <UserMenu
        user={auth.user}
        onSignOut={handleSignOut}
        onSignIn={handleReSignIn}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenAdmin={() => setTab('admin')}
      />
    </div>
  );

  // Sign-in nudge logic — fires once for skipAuth users when they've
  // accumulated meaningful progress. After dismiss, only Settings can re-trigger.
  const totalLibrary = state.snapshot.library.length;
  const streak = state.snapshot.profile.streak;
  let nudgeReason: string | null = null;
  if (state.snapshot.skipAuth && !state.snapshot.nudgeDismissed) {
    if (totalLibrary >= 10) nudgeReason = `You've saved ${totalLibrary} words. Sync them across devices.`;
    else if (streak >= 3) nudgeReason = `${streak}-day streak — don't lose it on this device.`;
  }

  // Boot-time side effects: observability, streak rollover, identify.
  // Gated on `ready` so we read the persisted snapshot, not EMPTY_SNAPSHOT —
  // otherwise bumpStreakIfNewDay operates on undefined lastActiveDay and the
  // resulting state is overwritten by the IndexedDB hydration that lands next.
  useEffect(() => {
    if (!state.ready) return;
    void initObservability().then(() => {
      track(Events.AppOpened);
      const uid = state.snapshot.profile.userId;
      if (uid) identify(uid);
    });
    state.bumpStreakIfNewDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.ready]);

  // Personas the user has matched (curated swipe-right OR auto-matched custom
  // persona created via "+"). Used for the story rings row and chat lookups.
  const matchedPersonas = useMemo(() => {
    const curated = PERSONAS.filter((p) => state.snapshot.relationships[p.id]?.matched);
    const customMatched = state.snapshot.customPersonas.filter(
      (p) => state.snapshot.relationships[p.id]?.matched,
    );
    return [...customMatched, ...curated];
  }, [state.snapshot.relationships, state.snapshot.customPersonas]);

  const openChat = useCallback(
    (personaId: string) => {
      const persona = findPersona(personaId);
      if (!persona) return;
      state.startChatIfMissing(persona);
      setChat({ personaId });
    },
    [state, findPersona],
  );

  if (!state.ready || !auth.ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.cream }}>
        <span className="ff-display text-3xl" style={{ color: C.ink }}>
          字
        </span>
      </div>
    );
  }

  // Password-reset recovery gate. Fires when the user arrived via a Supabase
  // reset-password email link. Sits above onboarding/sign-in so the one-time
  // recovery session never silently lands the user inside the app.
  if (auth.recovery) {
    return (
      <ErrorBoundary>
        <ResetPasswordScreen />
      </ErrorBoundary>
    );
  }

  // Fresh user, first time opening — show the pre-auth welcome screen with
  // its two-option choice (watch demo vs. jump to sign-in). Once they advance
  // past it (either path), hasSeenWelcome flips to true and this gate stays
  // closed. Existing users with a saved level are back-filled to
  // hasSeenWelcome=true by the v11 migration, so they never see this.
  if (!state.snapshot.hasSeenWelcome && !auth.user && !state.snapshot.skipAuth) {
    return (
      <ErrorBoundary>
        <OnboardingScreen onWelcomeDone={() => state.setHasSeenWelcome(true)} />
      </ErrorBoundary>
    );
  }

  // Past welcome but not signed in. Catches both fresh users who finished the
  // welcome flow AND returning skipAuth users who clicked "Sign in" from the
  // user menu (handleReSignIn flipped skipAuth back to false). Either way,
  // land directly on the standalone SignInScreen — never re-enter the welcome
  // flow.
  if (!auth.user && !state.snapshot.skipAuth) {
    return (
      <ErrorBoundary>
        <SignInScreen
          onSkip={() => {
            state.setSkipAuth(true);
            track(Events.Onboarded, { skipped: true });
          }}
        />
      </ErrorBoundary>
    );
  }

  // Brand-new signed-in user who hasn't completed (or explicitly skipped) the
  // profile setup. Shows an intro card with [Set me up] / [Skip for now] and
  // then runs the four profile questions. Returning users with profile.level
  // already set bypass this gate entirely.
  if (auth.user && !state.snapshot.profile.level && !state.snapshot.skippedSetup) {
    return (
      <ErrorBoundary>
        <ProfileSetupScreen
          onComplete={(patch) => {
            state.patchProfile(patch);
            if (patch.level) {
              track(Events.Onboarded, { level: patch.level, goals: patch.goals });
            }
          }}
          onSkip={() => {
            state.setSkippedSetup(true);
            track(Events.Onboarded, { skipped: true });
          }}
        />
      </ErrorBoundary>
    );
  }

  // Story modal sits above everything. Only curated personas have generated
  // stories; custom ones surface in rings as "open chat" affordances.
  const storyMap = dailyStories?.stories ?? null;
  const storyDate = dailyStories?.date ?? '';
  const storyPersona = story ? findPersona(story.personaId) : null;
  const storyList = storyPersona ? storiesFor(storyPersona.id, storyMap) : [];
  const storyChain = matchedPersonas.filter((p) => storiesFor(p.id, storyMap).length > 0);
  const flashcardable = state.library;

  return (
    <ErrorBoundary>
      <div className="h-[100dvh] flex flex-col overflow-hidden" style={{ background: C.cream }}>
        {tab === 'admin' ? (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full ff-body text-sm" style={{ color: C.inkLight }}>
                Loading dashboard…
              </div>
            }
          >
            <AdminDashboard onBack={() => setTab('matches')} />
          </Suspense>
        ) : chat ? (
          <ChatScreen
            persona={findPersona(chat.personaId)!}
            state={state.chatStates[chat.personaId]!}
            library={state.library}
            memory={state.snapshot.personaMemories[chat.personaId]}
            isFavorite={!!state.snapshot.favorites[chat.personaId]}
            onToggleFavorite={() => state.toggleFavorite(chat.personaId)}
            onBumpMemoryCounter={state.bumpMemoryCounter}
            onUpdatePersonaMemory={state.updatePersonaMemory}
            onBack={() => setChat(null)}
            onAppendMessage={(personaId, msg) => {
              state.appendMessage(personaId, msg);
              if (msg.from === 'me') track(Events.MessageSent, { personaId });
            }}
            onClearStoryContext={state.clearStoryContext}
            onSaveWord={(personaId, w) => {
              state.saveWord(w, personaId);
              push(`Saved ${w.hz}`, 'success');
              track(Events.WordSaved, { personaId, hz: w.hz });
            }}
            onOpenLibrary={() => {
              setChat(null);
              setTab('library');
            }}
          />
        ) : tab === 'discover' ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <AppHeader right={accountRight} />
            {nudgeReason && (
              <SignInNudge
                reason={nudgeReason}
                onSignIn={handleReSignIn}
                onDismiss={() => state.setNudgeDismissed(true)}
              />
            )}
            <div className="flex-1 overflow-hidden">
              <DiscoverScreen
                view={discoverView}
                onViewChange={setDiscoverView}
                relationships={state.snapshot.relationships}
                streak={state.snapshot.profile.streak}
                masteredCount={state.mastered.length}
                customPersonas={state.snapshot.customPersonas}
                onMatch={(p) => {
                  state.matchPersona(p);
                  track(Events.SwipedRight, { personaId: p.id });
                  track(Events.Matched, { personaId: p.id });
                }}
                onSkip={(p) => {
                  state.skipPersona(p);
                  track(Events.SwipedLeft, { personaId: p.id });
                }}
                onSwipeAgain={() => {
                  state.resetPassedPersonas();
                }}
                onOpenChat={openChat}
                onOpenProfile={setProfileOpen}
              />
            </div>
          </div>
        ) : tab === 'matches' ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <AppHeader right={accountRight} />
            <MatchesScreen
              matches={matchedPersonas}
              chatStates={state.chatStates}
              favorites={state.snapshot.favorites}
              relationships={state.snapshot.relationships}
              viewedStories={state.snapshot.viewedStories}
              storyDate={storyDate}
              dailyStories={storyMap}
              onOpenChat={openChat}
              onOpenStories={(personaId) => {
                if (storiesFor(personaId, storyMap).length === 0) {
                  openChat(personaId);
                  return;
                }
                setStory({ personaId, idx: 0 });
                state.markStoryViewed(personaId, storyDate);
                track(Events.StoryViewed, { personaId });
              }}
              onToggleFavorite={state.toggleFavorite}
              onCreateCharacter={() => setHskBuilderOpen(true)}
              onGoToDiscover={() => setTab('discover')}
            />
          </div>
        ) : tab === 'library' ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <AppHeader right={accountRight} />
            <LibraryScreen
              library={state.library}
              mastered={state.mastered}
              onOpenFlashcards={() => {
                setFlashcardOpen(true);
                track(Events.FlashcardSession, { size: flashcardable.length });
              }}
              onMarkKnown={(hz) => {
                state.markMastered(hz);
                push('Mastered!', 'success');
                track(Events.WordMastered, { hz });
              }}
              onSaveWord={(w) => {
                state.saveWord(w);
                push(`Saved ${w.hz}`, 'success');
                track(Events.WordSaved, { hz: w.hz });
              }}
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <AppHeader right={accountRight} />
            <HSKReviewScreen
              library={state.library}
              onSaveWord={(w) => {
                state.saveWord(w);
                push(`Saved ${w.hz}`, 'success');
              }}
              onOpenFlashcards={(level) => setHskReviewFlashcards(level)}
            />
          </div>
        )}

        {!chat && !flashcardOpen && hskReviewFlashcards === null && (
          <nav
            className="flex justify-around safe-bottom border-t"
            style={{ background: C.cream, borderColor: C.blush }}
            aria-label="Main navigation"
          >
            {([
              ['discover', Heart, 'Discover', '发现'],
              ['matches', MessageCircle, 'Chats', '聊天'],
              ['library', LibraryIcon, 'Library', '词库'],
              ['hsk', GraduationCap, 'HSK', 'HSK'],
            ] as const).map(([id, Icon, label, hz]) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className="flex-1 py-2 flex flex-col items-center gap-0.5 relative transition-colors"
                  aria-current={active ? 'page' : undefined}
                  style={{ color: active ? C.vermillion : C.inkSofter }}
                >
                  <Icon size={20} strokeWidth={active ? 2.4 : 1.8} aria-hidden />
                  <span className="ff-zh-serif text-[11px] font-semibold leading-none">{hz}</span>
                  <span className="ff-body text-[9px] opacity-80 leading-none">{label}</span>
                  <span
                    aria-hidden
                    className="absolute bottom-1 w-1 h-1 rounded-full transition-opacity"
                    style={{
                      background: C.vermillion,
                      opacity: active ? 1 : 0,
                    }}
                  />
                </button>
              );
            })}
          </nav>
        )}

        {storyPersona && story && storyList.length > 0 && (
          <StoryViewer
            persona={storyPersona}
            stories={storyList}
            idx={story.idx}
            library={state.snapshot.library}
            onAdvance={(delta) => {
              const next = story.idx + delta;
              if (next >= 0 && next < storyList.length) {
                setStory({ ...story, idx: next });
                return;
              }
              // Past the end (or before the start) — chain to neighbouring persona.
              const personaPos = storyChain.findIndex((p) => p.id === story.personaId);
              const neighbour = storyChain[personaPos + (delta > 0 ? 1 : -1)];
              if (!neighbour) {
                if (delta > 0) setStory(null);
                return;
              }
              const neighbourStories = storiesFor(neighbour.id, storyMap);
              const startIdx = delta > 0 ? 0 : neighbourStories.length - 1;
              setStory({ personaId: neighbour.id, idx: startIdx });
              state.markStoryViewed(neighbour.id, storyDate);
              track(Events.StoryViewed, { personaId: neighbour.id });
            }}
            onClose={() => setStory(null)}
            onReact={() => {
              state.bumpStoryWarmth(storyPersona.id);
              push('You reacted', 'success');
            }}
            onChat={(s) => {
              state.startChatIfMissing(storyPersona, s);
              track(Events.StoryReplied, { personaId: storyPersona.id });
              setStory(null);
              setChat({ personaId: storyPersona.id });
            }}
            onSaveWord={(w) => {
              state.saveWord(w, storyPersona.id);
              push(`Saved ${w.hz}`, 'success');
              track(Events.WordSaved, { hz: w.hz });
            }}
          />
        )}

        {flashcardOpen && (
          <FlashcardMode
            library={state.library}
            onClose={() => setFlashcardOpen(false)}
            onMarkKnown={(hz) => {
              state.markMastered(hz);
              push('Mastered!', 'success');
              track(Events.WordMastered, { hz });
            }}
          />
        )}

        {hskReviewFlashcards !== null && (
          <HSKReviewFlashcards
            level={hskReviewFlashcards}
            library={state.library}
            onClose={() => setHskReviewFlashcards(null)}
            onSaveWord={(w) => {
              state.saveWord(w);
              push(`Saved ${w.hz}`, 'success');
            }}
          />
        )}

        {hskBuilderOpen && (
          <HSKBuilder
            creating={creatingPersona}
            onClose={() => {
              if (creatingPersona) return;
              setHskBuilderOpen(false);
            }}
            onCreate={async (input) => {
              // Build first so we have a sane fallback if the AI greeting call
              // fails (canned HSK_GREETINGS via buildCustomPersona). Then try
              // to upgrade the greeting with an in-character opener from the
              // model before seeding the chat.
              const persona = buildCustomPersona(input);
              setCreatingPersona(true);
              const controller = new AbortController();
              const timeout = window.setTimeout(() => controller.abort(), 10_000);
              let greetingFailed = false;
              try {
                const reply = await getAIClient().greet(
                  {
                    persona,
                    library: state.snapshot.library,
                    hskCap: persona.customInput?.hskLevel ?? null,
                  },
                  controller.signal,
                );
                persona.greeting = {
                  hz: reply.hz,
                  py: reply.py,
                  en: reply.en,
                  words: reply.words,
                };
              } catch (err) {
                greetingFailed = true;
                console.warn('[hsk] greeting generation failed; using canned fallback', err);
              } finally {
                window.clearTimeout(timeout);
                setCreatingPersona(false);
              }
              state.addCustomPersona(persona);
              setHskBuilderOpen(false);
              if (greetingFailed) {
                push('Used a default opener — couldn’t reach the AI', 'warning');
              } else {
                push(`${persona.name} joined`, 'success');
              }
              setChat({ personaId: persona.id });
            }}
          />
        )}

        {conflict && (
          <ConflictPrompt
            local={conflict.local}
            server={conflict.server}
            onChoose={(side) => {
              const resolved = applyResolution(conflict, side);
              state.replaceSnapshot(resolved);
              setConflict(null);
              push(side === 'local' ? 'Kept this device’s data' : 'Kept cloud data', 'success');
            }}
          />
        )}

        {profileOpen && (
          <PersonaProfileSheet
            persona={profileOpen}
            onClose={() => setProfileOpen(null)}
            onStartChat={() => {
              const p = profileOpen;
              state.matchPersona(p);
              track(Events.Matched, { personaId: p.id });
              setProfileOpen(null);
              openChat(p.id);
            }}
          />
        )}

        {settingsOpen && (
          <SettingsSheet
            user={auth.user}
            onClose={() => setSettingsOpen(false)}
            onSignIn={() => {
              setSettingsOpen(false);
              handleReSignIn();
            }}
            onSignOut={() => {
              setSettingsOpen(false);
              void handleSignOut();
            }}
          />
        )}

        {feedbackOpen && (
          <FeedbackSheet
            onClose={() => setFeedbackOpen(false)}
            onSubmitted={() => push('Thanks — we got your note', 'success')}
          />
        )}

        {notificationsOpen && (
          <NotificationsSheet onClose={() => setNotificationsOpen(false)} />
        )}

        <Toast toast={toast} onDismiss={dismiss} />
      </div>
    </ErrorBoundary>
  );
}
